import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword, setup2FA, enable2FA, disable2FA, getMe, registerTelegramChatId, testTelegram } from '../services/api';

const Profilo = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [message, setMessage] = useState(null);
  
  // Password change
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 2FA
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  
  // Telegram
  const [telegramChatId, setTelegramChatId] = useState('');

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showMessage('Le password non coincidono', 'error');
      return;
    }
    
    if (newPassword.length < 6) {
      showMessage('La password deve essere di almeno 6 caratteri', 'error');
      return;
    }

    try {
      await changePassword(oldPassword, newPassword);
      showMessage('Password aggiornata con successo');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore cambio password', 'error');
    }
  };

  const handleSetup2FA = async () => {
    try {
      const res = await setup2FA();
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore setup 2FA', 'error');
    }
  };

  const handleEnable2FA = async () => {
    if (totpCode.length !== 6) {
      showMessage('Inserisci un codice di 6 cifre', 'error');
      return;
    }

    try {
      await enable2FA(totpCode);
      showMessage('2FA attivato con successo');
      setQrCode(null);
      setSecret(null);
      setTotpCode('');
      
      const res = await getMe();
      updateUser(res.data);
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Codice non valido', 'error');
    }
  };

  const handleDisable2FA = async () => {
    if (totpCode.length !== 6) {
      showMessage('Inserisci un codice di 6 cifre', 'error');
      return;
    }

    try {
      await disable2FA(totpCode);
      showMessage('2FA disattivato');
      setTotpCode('');
      
      const res = await getMe();
      updateUser(res.data);
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Codice non valido', 'error');
    }
  };

  const handleRegisterTelegram = async () => {
    if (!telegramChatId) {
      showMessage('Inserisci il Chat ID', 'error');
      return;
    }

    try {
      await registerTelegramChatId(telegramChatId);
      showMessage('Telegram configurato! Controlla il messaggio di conferma.');
      
      const res = await getMe();
      updateUser(res.data);
      setTelegramChatId('');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore configurazione Telegram', 'error');
    }
  };

  const handleTestTelegram = async () => {
    try {
      await testTelegram();
      showMessage('Messaggio di test inviato! Controlla Telegram.');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore invio test', 'error');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Profilo</h2>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <div 
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          👤 Informazioni
        </div>
        <div 
          className={`tab ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          🔑 Password
        </div>
        <div 
          className={`tab ${activeTab === '2fa' ? 'active' : ''}`}
          onClick={() => setActiveTab('2fa')}
        >
          📱 2FA
        </div>
        <div 
          className={`tab ${activeTab === 'telegram' ? 'active' : ''}`}
          onClick={() => setActiveTab('telegram')}
        >
          ✈️ Telegram
        </div>
      </div>

      {/* Tab Info */}
      {activeTab === 'info' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>Informazioni Account</h3>
          
          <table className="table" style={{ maxWidth: '500px' }}>
            <tbody>
              <tr>
                <td><strong>Username</strong></td>
                <td>{user?.username}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{user?.email}</td>
              </tr>
              <tr>
                <td><strong>Ruolo</strong></td>
                <td>{user?.is_admin ? 'Amministratore' : 'Utente'}</td>
              </tr>
              <tr>
                <td><strong>2FA</strong></td>
                <td>
                  <span 
                    className="ruota-badge"
                    style={{ 
                      background: user?.totp_enabled ? '#c6f6d5' : '#fed7d7',
                      color: user?.totp_enabled ? '#22543d' : '#822727'
                    }}
                  >
                    {user?.totp_enabled ? 'Attivo' : 'Non attivo'}
                  </span>
                </td>
              </tr>
              <tr>
                <td><strong>Telegram</strong></td>
                <td>
                  <span 
                    className="ruota-badge"
                    style={{ 
                      background: user?.telegram_chat_id ? '#c6f6d5' : '#fed7d7',
                      color: user?.telegram_chat_id ? '#22543d' : '#822727'
                    }}
                  >
                    {user?.telegram_chat_id ? 'Configurato' : 'Non configurato'}
                  </span>
                </td>
              </tr>
              <tr>
                <td><strong>Registrato il</strong></td>
                <td>{new Date(user?.created_at).toLocaleDateString('it-IT')}</td>
              </tr>
              <tr>
                <td><strong>Ultimo accesso</strong></td>
                <td>
                  {user?.last_login 
                    ? new Date(user.last_login).toLocaleString('it-IT')
                    : 'Mai'
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Tab Password */}
      {activeTab === 'password' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>Cambia Password</h3>
          
          <form onSubmit={handleChangePassword} style={{ maxWidth: '400px' }}>
            <div className="form-group">
              <label className="form-label">Password Attuale</label>
              <input
                type="password"
                className="form-input"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Nuova Password</label>
              <input
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Conferma Nuova Password</label>
              <input
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="btn btn-primary">
              Aggiorna Password
            </button>
          </form>
        </div>
      )}

      {/* Tab 2FA */}
      {activeTab === '2fa' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>
            Autenticazione a Due Fattori (2FA)
          </h3>
          
          {user?.totp_enabled ? (
            <div>
              <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                ✅ Il 2FA è attualmente <strong>attivo</strong> sul tuo account.
              </div>
              
              <p style={{ marginBottom: '15px' }}>
                Per disattivare il 2FA, inserisci il codice dalla tua app di autenticazione:
              </p>
              
              <div style={{ display: 'flex', gap: '10px', maxWidth: '300px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                />
                <button className="btn btn-danger" onClick={handleDisable2FA}>
                  Disattiva
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                Il 2FA aggiunge un ulteriore livello di sicurezza al tuo account.
              </div>
              
              {!qrCode ? (
                <button className="btn btn-primary" onClick={handleSetup2FA}>
                  Configura 2FA
                </button>
              ) : (
                <div>
                  <p style={{ marginBottom: '15px' }}>
                    Scansiona questo QR code con la tua app di autenticazione (Google Authenticator, Authy, etc.):
                  </p>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <img 
                      src={`data:image/png;base64,${qrCode}`} 
                      alt="QR Code 2FA"
                      style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                  </div>
                  
                  <p style={{ marginBottom: '10px', fontSize: '0.9rem', color: '#718096' }}>
                    Oppure inserisci manualmente questo codice: <code>{secret}</code>
                  </p>
                  
                  <p style={{ marginBottom: '15px' }}>
                    Inserisci il codice a 6 cifre mostrato nell'app:
                  </p>
                  
                  <div style={{ display: 'flex', gap: '10px', maxWidth: '300px' }}>
                    <input
                      type="text"
                      className="form-input"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                    />
                    <button className="btn btn-success" onClick={handleEnable2FA}>
                      Attiva
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab Telegram */}
      {activeTab === 'telegram' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>
            Notifiche Telegram
          </h3>
          
          {user?.telegram_chat_id ? (
            <div>
              <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                ✅ Telegram configurato! Riceverai notifiche su estrazioni e vincite.
              </div>
              
              <p style={{ marginBottom: '15px' }}>
                Chat ID attuale: <code>{user.telegram_chat_id}</code>
              </p>
              
              <button className="btn btn-secondary" onClick={handleTestTelegram}>
                📤 Invia messaggio di test
              </button>
              
              <hr style={{ margin: '20px 0' }} />
              
              <p style={{ marginBottom: '15px' }}>Vuoi cambiare Chat ID?</p>
              
              <div style={{ display: 'flex', gap: '10px', maxWidth: '400px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                  placeholder="Nuovo Chat ID"
                />
                <button className="btn btn-primary" onClick={handleRegisterTelegram}>
                  Aggiorna
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                Configura Telegram per ricevere notifiche su:
                <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                  <li>Nuove estrazioni</li>
                  <li>Vincite sulle tue giocate</li>
                  <li>Alert ritardi (numeri sopra soglia)</li>
                  <li>Verifica sistemi ridotti</li>
                </ul>
                <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#4a5568' }}>
                  <strong>Nota:</strong> Ogni utente riceve solo le proprie notifiche personali sul proprio Telegram.
                </p>
              </div>
           
              <h4 style={{ marginBottom: '15px' }}>Come configurare:</h4>
              
              <ol style={{ marginLeft: '20px', marginBottom: '20px', lineHeight: '2' }}>
                <li>Apri Telegram e cerca <code>@LottoLab_Evoforce_bot</code></li>
                <li>Avvia il bot con <strong>/start</strong></li>
                <li>Invia un messaggio qualsiasi al bot</li>
                <li>
                  Apri questo link per trovare il tuo Chat ID:<br />
                  <a 
                    href="https://api.telegram.org/bot8273906264:AAHdw2LvULpjTLzynIxVGt_xuasxwbiTuJQ/getUpdates" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}
                  >
                    https://api.telegram.org/bot8273906264:AAHdw.../getUpdates
                  </a>
                </li>
                <li>Cerca <code>"chat":{`{"id": XXXXXXX`}</code> - quel numero è il tuo Chat ID</li>
                <li>Inseriscilo qui sotto</li>
              </ol>
              
              <div style={{ display: 'flex', gap: '10px', maxWidth: '400px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                  placeholder="Es: 123456789"
                />
                <button className="btn btn-success" onClick={handleRegisterTelegram}>
                  Configura
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Profilo;
